import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader, Edit2, Trash2, ChevronLeft, ChevronRight, Phone, Search, X } from 'lucide-react';
import { useMyBusinesses } from '../../hooks/useMyBusinesses.js';
import { useFetchOnce } from '../../hooks/useFetchOnce.js';

const MyBusinesses = () => {
  const navigate = useNavigate();
  const vm = useMyBusinesses();

  const [viewMode, setViewMode] = useState('grid');

  const fetchOnce = useFetchOnce(() => {
    const savedPage = localStorage.getItem('myBusinessesCurrentPage');
    const pageToFetch = savedPage ? parseInt(savedPage, 10) : 1;
    return vm.fetchBusinesses(pageToFetch);
  });

  // Fetch initial page once on mount
  useState(() => {
    fetchOnce();
  });

  if (vm.loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 text-green-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {vm.error && (
        <div className="bg-red-50/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300">{vm.error}</p>
        </div>
      )}

      {vm.originalBusinesses.length > 0 && (
        <>
          {/* Search Bar and View Toggle */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            {/* Search Bar */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className="absolute left-3 sm:left-4 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search businesses..."
                value={vm.searchQuery}
                onChange={(e) => vm.setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-8 sm:pr-10 py-2 sm:py-2.5 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 backdrop-blur-sm transition-all text-sm"
              />
              {vm.searchQuery && (
                <button
                  onClick={() => vm.setSearchQuery('')}
                  className="absolute right-2 sm:right-3 top-2 sm:top-2.5 text-gray-400 hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* View Toggle Buttons - All Screen Sizes */}
            <div className="flex gap-2 flex-shrink-0 justify-center sm:justify-end">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold transition-all text-xs sm:text-sm ${viewMode === 'grid'
                    ? 'bg-green-500 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
              >
                Grid View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold transition-all text-xs sm:text-sm ${viewMode === 'list'
                    ? 'bg-green-500 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
              >
                List View
              </button>
            </div>
          </div>

          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {vm.businesses.length > 0 ? (
                vm.businesses.map((business) => (
                  <div
                    key={business.id || business._id}
                    className="bg-gradient-to-br from-white/10 to-white/5 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300 group border border-white/20 hover:border-green-400/50"
                  >
                    <div className="relative h-36 sm:h-48 bg-gray-700 overflow-hidden">
                      {business.coverImageUrl ? (
                        <img
                          src={business.coverImageUrl}
                          alt={business.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : business.avatar ? (
                        <img
                          src={business.avatar}
                          alt={business.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
                          <span className="text-gray-400 text-sm">No Image</span>
                        </div>
                      )}

                      <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
                        <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${business.status?.value === 'verified'
                            ? 'bg-green-500/90 text-white'
                            : 'bg-yellow-500/90 text-white'
                          }`}>
                          {business.status?.value === 'verified' ? '✓ Verified' : 'Unverified'}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-bold text-white mb-1 line-clamp-2">
                        {business.name}
                      </h3>

                      <p className="text-xs sm:text-sm text-green-400 font-semibold mb-2 sm:mb-3">
                        {business.category?.name || business.entity || 'Uncategorized'}
                      </p>

                      <p className="text-xs sm:text-sm text-gray-300 mb-2 sm:mb-3 line-clamp-2">
                        📍 {business.address || business.mapAddress?.generalAddress || 'No location'}
                      </p>

                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-300 mb-2 sm:mb-3 min-w-0">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{business.contact || 'N/A'}</span>
                      </div>

                      <div className="mb-3 sm:mb-4 inline-block">
                        <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-500/20 text-green-300 rounded-full text-xs sm:text-sm font-semibold border border-green-500/30">
                          {business.tag || business.kahaId}
                        </div>
                      </div>

                      <div className="flex gap-2 sm:gap-3">
                        <button
                          onClick={() => navigate(`/my-business/${business.id || business._id}`)}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg transition-all text-xs sm:text-sm"
                        >
                          View Profile
                        </button>
                      </div>

                      <div className="flex gap-2 sm:gap-3 mt-2 sm:mt-3">
                        <button
                          onClick={() => navigate(`/edit-business/${business.id || business._id}`)}
                          disabled={business.roles !== 'business_super_admin'}
                          className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg border transition-all text-xs sm:text-sm ${business.roles === 'business_super_admin'
                              ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border-blue-500/30 cursor-pointer'
                              : 'bg-gray-500/20 text-gray-400 border-gray-500/30 cursor-not-allowed opacity-50'
                            }`}
                          title={business.roles !== 'business_super_admin' ? 'Only business super admin can edit' : 'Edit business'}
                        >
                          <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          Edit
                        </button>
                        {business.status?.value !== 'verified' && (
                          <button
                            onClick={() => vm.handleDelete(business.id || business._id)}
                            className="flex-1 flex items-center justify-center gap-1 sm:gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg border border-red-500/30 transition-all text-xs sm:text-sm"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-400">No businesses match your search.</p>
                </div>
              )}
            </div>
          )}

          {viewMode === 'list' && (
            <div className="space-y-3 sm:space-y-4">
              {vm.businesses.length > 0 ? (
                vm.businesses.map((business) => (
                  <div
                    key={business.id || business._id}
                    className="bg-gradient-to-br from-white/10 to-white/5 rounded-lg sm:rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 p-3 sm:p-4 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 md:gap-6 border border-white/20 hover:border-green-400/50"
                  >
                    {business.coverImageUrl ? (
                      <img
                        src={business.coverImageUrl}
                        alt={business.name}
                        className="w-16 h-16 sm:w-20 sm:h-24 md:w-24 md:h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : business.avatar ? (
                      <img
                        src={business.avatar}
                        alt={business.name}
                        className="w-16 h-16 sm:w-20 sm:h-24 md:w-24 md:h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-24 md:w-24 md:h-24 rounded-lg bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs sm:text-xs text-gray-400">No Image</span>
                      </div>
                    )}

                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1">{business.name}</h3>
                      <p className="text-gray-400 text-xs sm:text-sm mb-2">{business.address || 'No address'}</p>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm min-w-0">
                        {business.contact && (
                          <span className="text-gray-300 truncate">📞 {business.contact}</span>
                        )}
                        {business.category && (
                          <span className="text-gray-300 flex items-center gap-2">
                            {business.category.iconUrl && <img src={business.category.iconUrl} alt={business.category.name} className="w-4 h-4 object-contain" />}
                            {business.category.name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 flex-shrink-0 w-full sm:w-auto">
                      <button
                        onClick={() => navigate(`/my-business/${business.id || business._id}`)}
                        className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-bold py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg transition-all text-xs sm:text-sm"
                      >
                        View Profile
                      </button>
                      <button
                        onClick={() => navigate(`/edit-business/${business.id || business._id}`)}
                        disabled={business.roles !== 'business_super_admin'}
                        className={`w-full sm:w-auto font-semibold py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg border transition-all text-xs sm:text-sm ${business.roles === 'business_super_admin'
                            ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border-blue-500/30 cursor-pointer'
                            : 'bg-gray-500/20 text-gray-400 border-gray-500/30 cursor-not-allowed opacity-50'
                          }`}
                        title={business.roles !== 'business_super_admin' ? 'Only business super admin can edit' : 'Edit business'}
                      >
                        <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      {business.status?.value !== 'verified' && (
                        <button
                          onClick={() => vm.handleDelete(business.id || business._id)}
                          className="w-full sm:w-auto bg-red-500/20 hover:bg-red-500/30 text-red-300 font-semibold py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg border border-red-500/30 transition-all text-xs sm:text-sm"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No businesses match your search.</p>
                </div>
              )}
            </div>
          )}

          {vm.totalPages > 1 && !vm.searchQuery && (
            <div className="flex justify-center items-center gap-2 mt-6 sm:mt-8 flex-wrap">
              <button
                onClick={() => vm.fetchBusinesses(vm.currentPage - 1)}
                disabled={vm.currentPage === 1}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg border border-white/20 transition-all text-xs sm:text-sm"
              >
                <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: vm.totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => vm.fetchBusinesses(page)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-semibold transition-all border text-xs sm:text-sm ${
                      vm.currentPage === page
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => vm.fetchBusinesses(vm.currentPage + 1)}
                disabled={vm.currentPage === vm.totalPages}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg border border-white/20 transition-all text-xs sm:text-sm"
              >
                Next
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {!vm.loading && vm.originalBusinesses.length === 0 && (
        <div className="text-center py-16">
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-12 backdrop-blur-xl border border-white/20 max-w-md mx-auto">
            <h3 className="text-2xl font-bold text-white mb-3">No Businesses</h3>
            <p className="text-gray-400 mb-6">You haven't registered any businesses yet.</p>
            <p className="text-gray-400 text-sm">Go to "Add Entity" tab to register your first business.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBusinesses;
